import React, { useEffect } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, Check } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { getProject, projects } from "../data/projects";

const CaseStudy = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const project = getProject(slug);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [slug]);

  if (!project) {
    return (
      <div className="min-h-screen">
        <Navbar />
        <div className="icg-container pt-40 pb-32">
          <p className="icg-eyebrow">
            <span className="icg-dot" /> 404
          </p>
          <h1
            className="font-display font-semibold tracking-tight mt-4"
            style={{ fontSize: "clamp(40px, 5vw, 72px)", lineHeight: 1 }}
          >
            Case study not found.
          </h1>
          <button
            onClick={() => navigate("/#work")}
            className="mt-8 icg-btn icg-btn--dark inline-flex"
          >
            <ArrowLeft size={16} /> Back to work
          </button>
        </div>
        <Footer />
      </div>
    );
  }

  const idx = projects.findIndex((p) => p.slug === project.slug);
  const nextProject = projects[(idx + 1) % projects.length];

  return (
    <div className="min-h-screen">
      <Navbar />

      <section className="pt-32 md:pt-40 pb-12">
        <div className="icg-container">
          <Link
            to="/#work"
            className="icg-link text-sm text-[color:var(--icg-muted)] inline-flex"
          >
            <ArrowLeft size={14} /> All work
          </Link>

          <div className="mt-8 flex flex-wrap gap-3 text-xs uppercase tracking-widest text-[color:var(--icg-muted)]">
            <span>{project.client}</span>
            <span>·</span>
            <span>{project.category}</span>
            <span>·</span>
            <span>{project.year}</span>
          </div>

          <h1
            className="font-display font-semibold tracking-tight mt-4"
            style={{ fontSize: "clamp(44px, 7vw, 108px)", lineHeight: 0.98 }}
          >
            {project.title}
          </h1>

          <p
            className="mt-6 font-serif-it text-[color:var(--icg-accent)]"
            style={{ fontSize: "clamp(20px, 2.2vw, 30px)" }}
          >
            {project.tagline}
          </p>
        </div>
      </section>

      <div className="icg-container">
        <div
          className="relative overflow-hidden rounded-3xl bg-[color:var(--icg-cream-soft)] border border-[color:var(--icg-line)]"
        >
          <img
            src={project.cover}
            alt={project.title}
            className="w-full h-auto object-cover"
            style={{ maxHeight: 640 }}
          />
        </div>
      </div>

      <section className="icg-section">
        <div className="icg-container grid md:grid-cols-12 gap-10">
          <aside className="md:col-span-4 md:sticky md:top-28 self-start">
            <div className="space-y-6">
              <div>
                <div className="text-xs uppercase tracking-widest text-[color:var(--icg-muted)]">
                  Role
                </div>
                <div className="mt-1 font-display font-medium">
                  {project.role}
                </div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-widest text-[color:var(--icg-muted)]">
                  Team
                </div>
                <ul className="mt-1 space-y-1">
                  {project.team.map((m) => (
                    <li key={m} className="text-sm">
                      {m}
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <div className="text-xs uppercase tracking-widest text-[color:var(--icg-muted)]">
                  Duration
                </div>
                <div className="mt-1 font-display font-medium">
                  {project.duration}
                </div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-widest text-[color:var(--icg-muted)]">
                  Discipline
                </div>
                <div className="mt-2 flex flex-wrap gap-2">
                  {project.tags.map((tg) => (
                    <span
                      key={tg}
                      className="text-xs px-3 py-1 rounded-full border border-[color:var(--icg-line)]"
                    >
                      {tg}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </aside>

          <div className="md:col-span-8 space-y-14">
            <div>
              <div className="text-xs uppercase tracking-widest text-[color:var(--icg-accent)]">
                01 · Problem
              </div>
              <h2 className="mt-2 font-display font-semibold text-3xl md:text-4xl tracking-tight">
                The starting point
              </h2>
              <p className="mt-4 text-lg leading-relaxed text-[color:var(--icg-ink-soft)]">
                {project.problem}
              </p>
            </div>

            <div>
              <div className="text-xs uppercase tracking-widest text-[color:var(--icg-accent)]">
                02 · Approach
              </div>
              <h2 className="mt-2 font-display font-semibold text-3xl md:text-4xl tracking-tight">
                How we worked
              </h2>
              <p className="mt-4 text-lg leading-relaxed text-[color:var(--icg-ink-soft)]">
                {project.approach}
              </p>
            </div>

            <div>
              <div className="text-xs uppercase tracking-widest text-[color:var(--icg-accent)]">
                03 · Outcome
              </div>
              <h2 className="mt-2 font-display font-semibold text-3xl md:text-4xl tracking-tight">
                What we delivered
              </h2>
              <p className="mt-4 text-lg leading-relaxed text-[color:var(--icg-ink-soft)]">
                {project.outcome}
              </p>

              <ul className="mt-8 grid sm:grid-cols-3 gap-4">
                {project.highlights.map((h) => (
                  <li
                    key={h}
                    className="p-5 rounded-2xl bg-[color:var(--icg-cream-soft)] border border-[color:var(--icg-line)]"
                  >
                    <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-[color:var(--icg-ink)] text-[color:var(--icg-cream)]">
                      <Check size={14} />
                    </span>
                    <div className="mt-3 font-display font-medium">{h}</div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="pb-24">
        <div className="icg-container">
          <div className="rounded-3xl bg-[color:var(--icg-ink)] text-[color:var(--icg-cream)] p-10 md:p-14 flex flex-col md:flex-row md:items-center md:justify-between gap-8">
            <div>
              <div className="text-xs uppercase tracking-widest opacity-60">
                Next case study
              </div>
              <h3 className="mt-2 font-display font-semibold tracking-tight text-3xl md:text-5xl">
                {nextProject.title}
              </h3>
              <p className="mt-2 opacity-70">
                {nextProject.client} · {nextProject.category} · {nextProject.year}
              </p>
            </div>
            <Link
              to={`/work/${nextProject.slug}`}
              className="icg-btn"
              style={{
                background: "var(--icg-accent)",
                color: "var(--icg-ink)",
              }}
            >
              Read case study <ArrowUpRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default CaseStudy;
